import { useEffect, useState } from "react";
import { clearStorageKeepingAttribution } from "../../../utils/attribution";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import {
  IconSettings,
  IconAdjustmentsHorizontal,
  IconDatabase,
  IconBrandWhatsapp,
  IconBellRinging,
  IconPlayerPause,
  IconPlayerPlay,
  IconEdit,
  IconTrash,
  IconFileZip,
  IconDownload,
  IconFileText,
  IconAlertTriangle,
  IconInfoCircle,
  IconX,
  IconCircleCheck,
} from "@tabler/icons-react";
import classes from "./settingsRedesign.module.css";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";
import TagsInput from "../../../components/UI/TagsInput/tags";
import { ALL_SKILLS } from "../../../constants";
import {
  talentSuspendAccount,
  talentUnsuspendAccount,
  talentDeleteAccount,
  talentWhatsappNotification,
  talentProfile,
  downloadAccountPDF,
  downloadJobDataPDF,
} from "../../../services/talentCalls";
import { errorHandler, toastSuccess } from "../../../utils";

const Toggle = ({ checked, onChange, disabled, noTransition }) => (
  <label className={classes.toggleSwitch}>
    <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
    <span className={`${classes.toggleSlider} ${noTransition ? classes.toggleSliderNoTransition : ""}`} />
  </label>
);

const TalentSettings = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("config");

  const [whatsappNotification, setWhatsappNotification] = useState(null);
  const [whatsappLoaded, setWhatsappLoaded] = useState(false);
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [whatsappTouched, setWhatsappTouched] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await talentProfile();
        if (res?.data?.data) setWhatsappNotification(!!res.data.data.whatsappNotificationEnabled);
      } catch (_) {
      } finally {
        setWhatsappLoaded(true);
      }
    };
    fetchProfile();
  }, []);

  const handleWhatsappNotification = async () => {
    if (whatsappSaving || !whatsappLoaded) return;
    setWhatsappTouched(true);
    setWhatsappSaving(true);
    const nextValue = !whatsappNotification;
    setWhatsappNotification(nextValue);

    try {
      const res = await talentWhatsappNotification();
      if (res?.data?.success) {
        toastSuccess(
          res?.data?.data?.message ||
            (nextValue ? "WhatsApp notifications enabled successfully" : "WhatsApp notifications disabled successfully"),
        );
        setWhatsappNotification(res?.data?.data?.whatsappEnabled);
      }
    } catch (error) {
      setWhatsappNotification(!nextValue);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong. Please check your internet connection and try again.", "Error");
    } finally {
      setWhatsappSaving(false);
    }
  };

  const [notificationsPaused, setNotificationsPaused] = useState(false);
  const [editAlertsModal, setEditAlertsModal] = useState(false);
  const [alertSkills, setAlertSkills] = useState([]);

  const [suspendModal, setSuspendModal] = useState(false);
  const [suspendDate, setSuspendDate] = useState(null);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [justSuspended, setJustSuspended] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [password, setPassword] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [loadingAccountPdf, setLoadingAccountPdf] = useState(false);
  const [loadingJobsPdf, setLoadingJobsPdf] = useState(false);

  const handleSuspend = async () => {
    if (!suspendDate) return;
    setSuspendLoading(true);
    try {
      await talentSuspendAccount({ dateSuspended: suspendDate.toISOString() });
      setSuspendModal(false);
      setJustSuspended(true);
      toastSuccess("Account suspended");
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    try {
      await talentUnsuspendAccount();
      setJustSuspended(false);
      toastSuccess("Account reactivated");
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!password) {
      setDeleteError("Please enter your password to confirm.");
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await talentDeleteAccount({ password });
      if (res?.data?.success) {
        clearStorageKeepingAttribution();
        navigate("/login", { replace: true });
      }
    } catch (error) {
      setDeleteError(
        error?.response?.data?.errors || "Failed to delete account. Please check your password.",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDownload = async (downloadFn, setLoading, fileName, emptyMessage) => {
    setLoading(true);
    try {
      const response = await downloadFn();
      const blob = new Blob([response?.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error?.response?.data instanceof Blob) {
        const text = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsText(error?.response?.data);
        });
        const errorData = JSON.parse(text);
        errorHandler(errorData?.errors === "No completed jobs found." ? emptyMessage : errorData?.errors);
      } else {
        errorHandler(error?.message || "Download failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.main}>
      {!whatsappLoaded && <FullScreenLoader />}

      <div className={classes.pageHeader}>
        <span className={classes.eyebrow}>
          <IconSettings size={14} /> Settings
        </span>
        <h1 className={classes.pageTitle}>Manage your account.</h1>
        <p className={classes.pageSubtitle}>
          Control your notifications, privacy, and data — or take a break whenever you need to.
        </p>
      </div>

      <div className={classes.tabbar}>
        <button
          type="button"
          className={`${classes.tab} ${tab === "config" ? classes.tabActive : ""}`}
          onClick={() => setTab("config")}
        >
          <IconAdjustmentsHorizontal size={16} /> Configurations
        </button>
        <button
          type="button"
          className={`${classes.tab} ${tab === "data" ? classes.tabActive : ""}`}
          onClick={() => setTab("data")}
        >
          <IconDatabase size={16} /> Account Data
        </button>
      </div>

      {tab === "config" && (
        <div className={classes.stack}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.cardHeaderLeft}>
                <div className={classes.cardIcon}>
                  <IconBrandWhatsapp size={18} />
                </div>
                <div className={classes.cardTitle}>Notifications</div>
              </div>
            </div>
            <div className={classes.cardBody}>
              <div className={classes.toggleRow}>
                <div className={classes.toggleInfo}>
                  <div className={classes.toggleLabel}>Get Alerts on WhatsApp</div>
                  <div className={classes.toggleDesc}>
                    Receive job match and application alerts on WhatsApp in addition to email.
                  </div>
                </div>
                <Toggle
                  checked={!!whatsappNotification}
                  onChange={handleWhatsappNotification}
                  disabled={whatsappSaving}
                  noTransition={!whatsappTouched}
                />
              </div>

              <div className={classes.actionRow}>
                <p className={classes.actionText}>Update which skills trigger job alerts for you.</p>
                <div className={classes.actionButtons}>
                  <button className={classes.btnOutlineAmber} onClick={() => setEditAlertsModal(true)}>
                    <IconEdit size={14} /> Edit Alerts
                  </button>
                </div>
              </div>

              <div className={classes.actionRow}>
                <p className={classes.actionText}>
                  {notificationsPaused
                    ? "Your notifications are paused. Resume any time to start receiving alerts again."
                    : "Pause your notifications any time you need a break — you can resume whenever you're ready."}
                </p>
                <div className={classes.actionButtons}>
                  <button className={classes.btnOutlineAmber} onClick={() => setNotificationsPaused((p) => !p)}>
                    {notificationsPaused ? <IconPlayerPlay size={14} /> : <IconPlayerPause size={14} />}
                    {notificationsPaused ? "Resume Notifications" : "Pause Notifications"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.cardHeaderLeft}>
                <div className={`${classes.cardIcon} ${classes.cardIconAmber}`}>
                  <IconPlayerPause size={18} />
                </div>
                <div className={classes.cardTitle}>Suspend Account</div>
              </div>
            </div>
            <div className={classes.cardBody}>
              <div className={classes.actionRow}>
                <p className={classes.actionText}>
                  {justSuspended
                    ? "Your account is suspended. We'll automatically re-activate it once the suspension period expires, or you can reactivate it yourself any time before then."
                    : "We'll automatically re-activate your account once the suspension period expires. As an alternative, you can always come back here before the expiration date to manually re-activate it yourself."}
                </p>
                <div className={classes.actionButtons}>
                  {justSuspended ? (
                    <button className={classes.btnOutlineAmber} onClick={handleReactivate} disabled={reactivateLoading}>
                      <IconCircleCheck size={14} /> {reactivateLoading ? "Reactivating…" : "Reactivate Account"}
                    </button>
                  ) : (
                    <button className={classes.btnOutlineAmber} onClick={() => setSuspendModal(true)}>
                      <IconPlayerPause size={14} /> Suspend Account
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={`${classes.card} ${classes.dangerCard}`}>
            <div className={classes.cardHeader}>
              <div className={classes.cardHeaderLeft}>
                <div className={`${classes.cardIcon} ${classes.cardIconDanger}`}>
                  <IconTrash size={18} />
                </div>
                <div className={`${classes.cardTitle} ${classes.dangerTitle}`}>Delete Account</div>
              </div>
            </div>
            <div className={classes.cardBody}>
              <div className={classes.actionRow}>
                <p className={classes.actionText}>
                  Please let us know if there's an issue so we can help you solve it — we're sorry to let you go. This
                  action is permanent and cannot be undone.
                </p>
                <div className={classes.actionButtons}>
                  <button className={classes.btnOutlineDanger} onClick={() => setDeleteModal(true)}>
                    <IconTrash size={14} /> Delete My Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "data" && (
        <div className={classes.stack}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.cardHeaderLeft}>
                <div className={classes.cardIcon}>
                  <IconFileZip size={18} />
                </div>
                <div className={classes.cardTitle}>Download Account Data</div>
              </div>
            </div>
            <div className={classes.cardBody}>
              <div className={classes.docRow}>
                <div className={classes.docIcon}>
                  <IconFileZip size={19} />
                </div>
                <div className={classes.docInfo}>
                  <div className={classes.docName}>Full account export</div>
                  <div className={classes.docDesc}>
                    Your data belongs to you — download it here. Includes your profile info, applications, and activity
                    history.
                  </div>
                </div>
                <button
                  className={classes.btnOutlineAmber}
                  disabled={loadingAccountPdf}
                  onClick={() =>
                    handleDownload(downloadAccountPDF, setLoadingAccountPdf, "accountData", "Failed to download account data")
                  }
                >
                  <IconDownload size={14} /> {loadingAccountPdf ? "Downloading…" : "Download Data"}
                </button>
              </div>
            </div>
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.cardHeaderLeft}>
                <div className={classes.cardIcon}>
                  <IconFileText size={18} />
                </div>
                <div className={classes.cardTitle}>Download Jobs Data</div>
              </div>
            </div>
            <div className={classes.cardBody}>
              <div className={classes.docRow}>
                <div className={classes.docIcon}>
                  <IconFileText size={19} />
                </div>
                <div className={classes.docInfo}>
                  <div className={classes.docName}>Jobs &amp; recommendations summary</div>
                  <div className={classes.docDesc}>
                    You can use a summary of your jobs on this platform as recommendations to apply for jobs outside
                    our platform.
                  </div>
                </div>
                <button
                  className={classes.btnOutlineAmber}
                  disabled={loadingJobsPdf}
                  onClick={() =>
                    handleDownload(
                      downloadJobDataPDF,
                      setLoadingJobsPdf,
                      "jobData",
                      "You need to complete at least one job to download job data",
                    )
                  }
                >
                  <IconDownload size={14} /> {loadingJobsPdf ? "Downloading…" : "Download Recommendations"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editAlertsModal && (
        <div className={classes.modalBackdrop} onClick={() => setEditAlertsModal(false)}>
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={classes.modalHeadIcon}>
                <IconBellRinging size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Edit Alerts</div>
                <div className={classes.modalHeadSub}>Choose which skills should trigger a job alert for you.</div>
              </div>
              <button className={classes.modalClose} onClick={() => setEditAlertsModal(false)} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.confirmField}>
                <label className={classes.confirmLabel}>Add or edit skills</label>
                <TagsInput
                  required
                  data={alertSkills}
                  setData={setAlertSkills}
                  defaultData={ALL_SKILLS}
                  placeholder="Type to search"
                  style={{ position: "inherit" }}
                />
              </div>
            </div>
            <div className={classes.modalFoot}>
              <button className={`${classes.modalBtn} ${classes.modalBtnGhost}`} onClick={() => setEditAlertsModal(false)}>
                Update changes
              </button>
            </div>
          </div>
        </div>
      )}

      {suspendModal && (
        <div className={classes.modalBackdrop} onClick={() => setSuspendModal(false)}>
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={`${classes.modalHeadIcon} ${classes.modalHeadIconAmber}`}>
                <IconPlayerPause size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Suspend your account?</div>
                <div className={classes.modalHeadSub}>You can undo this any time before the suspension expires.</div>
              </div>
              <button className={classes.modalClose} onClick={() => setSuspendModal(false)} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.modalNote}>
                <IconInfoCircle size={16} />
                While suspended, your profile won't be visible to recruiters and you won't receive new job matches.
                We'll automatically re-activate your account once the suspension period expires — or come back here
                any time to reactivate it yourself.
              </div>
              <div className={classes.confirmField}>
                <label className={classes.confirmLabel}>Suspend until</label>
                <DatePicker
                  selected={suspendDate}
                  onChange={setSuspendDate}
                  className={classes.confirmInput}
                  dateFormat="EEEE, do MMMM yyyy"
                  minDate={new Date()}
                  placeholderText="Select a date"
                />
              </div>
            </div>
            <div className={classes.modalFoot}>
              <button className={`${classes.modalBtn} ${classes.modalBtnGhost}`} onClick={() => setSuspendModal(false)}>
                Cancel
              </button>
              <button
                className={`${classes.modalBtn} ${classes.modalBtnWarning}`}
                onClick={handleSuspend}
                disabled={!suspendDate || suspendLoading}
              >
                <IconPlayerPause size={14} /> {suspendLoading ? "Suspending…" : "Suspend Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div
          className={classes.modalBackdrop}
          onClick={() => {
            setDeleteModal(false);
            setDeleteError(null);
          }}
        >
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={`${classes.modalHeadIcon} ${classes.modalHeadIconDanger}`}>
                <IconAlertTriangle size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Delete your account?</div>
                <div className={classes.modalHeadSub}>This action is permanent and cannot be undone.</div>
              </div>
              <button
                className={classes.modalClose}
                onClick={() => {
                  setDeleteModal(false);
                  setDeleteError(null);
                }}
                aria-label="Close"
              >
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={`${classes.modalNote} ${classes.modalNoteDanger}`}>
                <IconAlertTriangle size={16} />
                Deleting your account permanently removes your profile, applications, saved matches, and documents.
                Please let us know if there's an issue so we can help you solve it — we're sorry to see you go.
              </div>
              <div className={classes.confirmField}>
                <label className={classes.confirmLabel}>Enter your password to confirm</label>
                <input
                  type="password"
                  className={classes.confirmInput}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setDeleteError(null);
                  }}
                  placeholder="Password"
                  autoComplete="off"
                />
              </div>
              {deleteError && <p className={classes.modalError}>{deleteError}</p>}
            </div>
            <div className={classes.modalFoot}>
              <button
                className={`${classes.modalBtn} ${classes.modalBtnGhost}`}
                onClick={() => {
                  setDeleteModal(false);
                  setDeleteError(null);
                }}
              >
                Cancel
              </button>
              <button
                className={`${classes.modalBtn} ${classes.modalBtnDanger}`}
                onClick={handleDelete}
                disabled={!password || deleteLoading}
              >
                <IconTrash size={14} /> {deleteLoading ? "Deleting…" : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentSettings;
